import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Grid,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { parseISO, format } from "date-fns";

// Define the interface for event data
interface EventData {
  event_id: string;
  name: string;
  location: string;
  soldTickets: number;
  image: string;
  percentage: number;
}

// Colors for the pie chart slices
const COLORS = ["#4D67EB", "#94A8F9", "#788EF3", "#5DADE2", "#58D68D"];

// Styles for the percentage text in the center of the pie chart
const centerStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  textAlign: "center" as "center",
};

// Styled Paper component to remove box shadow and add padding
const StyledPaper = (props: any) => (
  <Paper
    elevation={0}
    sx={{
      padding: { xs: 2, sm: 4 },
      backgroundColor: "#FFFFFF",
      position: "relative",
      borderRadius: 2,
      boxShadow: 1,
    }}
    {...props}
  />
);

const SalesManagementComponent: React.FC = () => {
  const theme = useTheme(); // Access MUI theme for responsive design
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Determine if screen is small
  const { t } = useTranslation();
  // Get ordersList from Redux state
  const { ordersList } = useSelector(
    (state: RootState) => state?.salesManagementReducer
  );

  // Filter state
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  // Helper functions for month handling
  const getMonthKey = useCallback((dateString: string): string => {
    try {
      return format(parseISO(dateString), 'yyyy-MM');
    } catch {
      return '';
    }
  }, []);

  const getMonthDisplay = useCallback((monthKey: string): string => {
    try {
      return format(parseISO(`${monthKey}-01`), 'MMMM yyyy');
    } catch {
      return monthKey;
    }
  }, []);

  const isOrderInMonth = useCallback((orderDate: string, monthKey: string): boolean => {
    return getMonthKey(orderDate) === monthKey;
  }, [getMonthKey]);

  // Generate filter options
  const monthOptions = useMemo(() => {
    const monthSet = new Set<string>();
    ordersList?.forEach((order: any) => {
      if (order.po_date) {
        monthSet.add(getMonthKey(order.po_date));
      }
    });

    return Array.from(monthSet)
      .sort((a, b) => b.localeCompare(a)) // Descending
      .map(key => ({
        key,
        display: getMonthDisplay(key)
      }));
  }, [ordersList, getMonthKey, getMonthDisplay]);

  const eventOptions = useMemo(() => {
    const eventMap = new Map();
    ordersList?.forEach((order: any) => {
      if (!eventMap.has(order.event_id)) {
        eventMap.set(order.event_id, {
          id: order.event_id,
          name: order.eventName_en || "Unknown Event"
        });
      }
    });
    return Array.from(eventMap.values());
  }, [ordersList]);

  // Event handlers
  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value);
  };

  const handleEventChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedEvents(typeof value === 'string' ? value.split(',') : value);
  };

  // Apply filters to orders
  const filteredOrders = useMemo(() =>
    ordersList?.filter((order: any) => {
      // Month filter
      if (selectedMonth && !isOrderInMonth(order.po_date, selectedMonth)) {
        return false;
      }

      // Event filter
      if (selectedEvents.length > 0 && !selectedEvents.includes(order.event_id)) {
        return false;
      }

      return true;
    }),
    [ordersList, selectedMonth, selectedEvents, isOrderInMonth]
  );

  // Aggregate filtered orders
  const { eventDataArray, pieData, totalSoldTickets } = useMemo(() => {
    const eventDataMap: { [key: string]: EventData } = {};
    let total = 0;

    filteredOrders?.forEach((order: any) => {
      const eventId = order.event_id;
      const soldTickets = Number(order.sold_ticket) || 0;
      total += soldTickets;

      if (!eventDataMap[eventId]) {
        eventDataMap[eventId] = {
          event_id: eventId,
          name: order.eventName_en || "Unknown Event",
          location: order.location || "Unknown Location",
          soldTickets: 0,
          image: order.eventImage || "https://via.placeholder.com/40",
          percentage: 0,
        };
      }

      eventDataMap[eventId].soldTickets += soldTickets;
    });

    const dataArray = Object.values(eventDataMap);

    // Calculate percentages
    const totalForPercentage = total || 1; // Avoid division by zero
    dataArray.forEach((event) => {
      event.percentage = Number(
        ((event.soldTickets / totalForPercentage) * 100).toFixed(2)
      );
    });

    // Sort by tickets descending
    dataArray.sort((a, b) => b.soldTickets - a.soldTickets);

    // Generate pie data
    const pie = dataArray.map((event) => ({
      name: event.name,
      value: event.soldTickets,
    }));

    return { eventDataArray: dataArray, pieData: pie, totalSoldTickets: total };
  }, [filteredOrders]);

  return (
    <Box
      sx={{
        padding: "0 3vw",
        minHeight: "90vh",
        minWidth: "85vw",
        backgroundColor: "#f0f2f5",
      }}
    >
      {/* Title Section */}
      <Box>
        <Typography
          variant={isSmallScreen ? "h6" : "h5"}
          gutterBottom
          sx={{
            fontWeight: "bold",
            textAlign: isSmallScreen ? "center" : "left",
          }}
        >
          {t("Sales Statistics")}
        </Typography>
        <Typography
          variant="subtitle1"
          color="textSecondary"
          sx={{
            textAlign: isSmallScreen ? "center" : "left",
          }}
        >
          {t("Summary of ticket sales by event")}
        </Typography>
      </Box>

      {/* Filters Section */}
      <Box
        sx={{
          marginTop: 3,
          marginBottom: 2,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: 2,
          alignItems: isSmallScreen ? "stretch" : "center",
          justifyContent: "flex-end",
        }}
      >
        {/* Month Filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="month-select-label">{t("Month")}</InputLabel>
          <Select
            labelId="month-select-label"
            value={selectedMonth}
            label={t("Month")}
            onChange={handleMonthChange}
          >
            <MenuItem value="">{t("All Months")}</MenuItem>
            {monthOptions.map((month) => (
              <MenuItem key={month.key} value={month.key}>
                {month.display}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Event Filter */}
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="event-select-label">{t("Events")}</InputLabel>
          <Select
            labelId="event-select-label"
            multiple
            value={selectedEvents}
            label={t("Events")}
            onChange={handleEventChange}
            renderValue={(selected) =>
              selected.length === 0
                ? t("All Events")
                : `${selected.length} ${selected.length === 1 ? t("event") : t("events")}`
            }
          >
            {eventOptions.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                <Checkbox checked={selectedEvents.indexOf(event.id) > -1} />
                <ListItemText primary={event.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Content Section */}
      <Box sx={{ marginTop: 4 }}>
        <StyledPaper>
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="space-between"
            direction={isSmallScreen ? "column" : "row"}
          >
            {/* Pie Chart Section */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: isSmallScreen ? 300 : 450,
                }}
              >
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={isSmallScreen ? 100 : 140}
                      innerRadius={isSmallScreen ? 60 : 100}
                      paddingAngle={5}
                      cornerRadius={10}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS?.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                {/* Centered Total Tickets Sold */}
                <Box sx={centerStyle}>
                  <Typography
                    variant={isSmallScreen ? "h4" : "h3"}
                    component="div"
                    fontWeight="bold"
                  >
                    {totalSoldTickets}
                  </Typography>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Tickets Sold
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Summary List Section */}
            <Grid item xs={12} md={5}>
              <List>
                {eventDataArray?.map((event, index) => (
                  <ListItem
                    key={event.event_id}
                    sx={{
                      marginBottom: 2,
                      paddingLeft: 0,
                      paddingRight: 0,
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      flexDirection: isSmallScreen ? "column" : "row",
                      alignItems: isSmallScreen ? "flex-start" : "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: isSmallScreen ? "100%" : "70%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ListItemAvatar>
                          <Avatar
                            src={event.image}
                            alt={`${event.name} poster`}
                            sx={{
                              borderRadius: "8px",
                              width: 40,
                              height: 40,
                              mr: isSmallScreen ? 0 : 2,
                              mb: isSmallScreen ? 1 : 0,
                            }}
                          />
                        </ListItemAvatar>
                        <Box
                          sx={{
                            marginLeft: isSmallScreen ? 0 : 1,
                            textAlign: isSmallScreen ? "left" : "left",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{
                              fontSize: isSmallScreen ? "1rem" : "1.1rem",
                            }}
                          >
                            {event.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
                            }}
                          >
                            {event.location}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="subtitle1"
                        color="#5483F9"
                        sx={{
                          fontSize: isSmallScreen ? "1rem" : "1.1rem",
                          marginLeft: isSmallScreen ? 2 : 0,
                          marginTop: isSmallScreen ? 1 : 0,
                        }}
                      >
                        {event.soldTickets} tickets
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </StyledPaper>
      </Box>
    </Box>
  );
};

export default SalesManagementComponent;
